import fallbackAvatarImage from '@/assets/images/default-avatar.jpg';
import { Button } from '@/components/Button';
import { Image } from '@/components/Image';
import { Loading } from '@/components/Loading';
import { PremiumFeatureOverlay } from '@/components/PremiumFeatureOverlay';
import { Text } from '@/components/Text';
import { LinkIcon } from '@/partials/dashboard/Cards/UpdateCard/UpdateCardLinks/LinkIcon';
import { useAuthStore } from '@/stores/useAuthStore';
import { useCardStore } from '@/stores/useCardStore';
import getDefaultCover from '@/utils/getDefaultCover';
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaExchangeAlt, FaUserPlus } from 'react-icons/fa';
import { MdShare } from 'react-icons/md';

export const UpdateCardPhone = () => {
  const { card, selectedMenuItem } = useCardStore();
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const [isLimitExceeded, setIsLimitExceeded] = useState(false);

  // Vérifier si les limites de visites sont dépassées
  useEffect(() => {
    if (!card) return;

    // Si le nombre de visites dépasse la limite, activer le flou
    if (card.visits_exceeded) {
      setIsLimitExceeded(true);
    } else {
      setIsLimitExceeded(false);
    }
  }, [card]);

  // Vérifier si nous sommes sur l'onglet "lead-capture" (id 3 dans le menu)
  const isLeadCaptureTab = selectedMenuItem === 3;

  if (!card) return <Loading />;

  const cardColor = card.color;
  const defaultCover = getDefaultCover(cardColor);

  // Textes personnalisés pour le lead capture
  const leadCaptureTitle =
    card.lead_capture?.title || 'On échange nos coordonnées ?';
  const leadCaptureDescription =
    card.lead_capture?.description ||
    'Remplissez le formulaire ci-dessous pour me partager vos coordonnées.';
  const leadCaptureButtonText = card.lead_capture?.button_text || 'Envoyer';
  const leadCaptureDisclaimer = card.lead_capture?.disclaimer;

  const phoneContent = (
    <motion.div
      className="relative aspect-[290/620] w-[290px] max-w-full overflow-hidden rounded-[45px] border-[3px] border-black bg-white lg:mt-0 lg:w-[280px] xl:w-[290px]"
      whileHover={{ y: -5 }}
    >
      {/* Dynamic Island */}
      <motion.div className="absolute left-1/2 top-4 z-10 h-[25px] w-[85px] -translate-x-1/2 rounded-full bg-black"></motion.div>
      {/* Phone frame */}
      <div
        className="relative size-full"
        style={{ backgroundColor: cardColor }}
      >
        {/* Scrollable content */}
        <div className="h-[calc(100%-8px)] overflow-y-auto">
          {/* Content wrapper */}
          <div className="relative">
            {/* Header with Banner */}
            <div className="relative h-[163px] w-full">
              {/* Banner Image with ClipPath */}
              <motion.div className="h-[163px] w-full overflow-hidden">
                <Image
                  src={card.cover}
                  defaultImage={defaultCover}
                  alt="Banner"
                  className="h-[163px] w-full object-cover"
                  refreshKey={card.updated_at}
                />
                {/* Overlay avec dégradé subtil en bas */}
                <div
                  className="absolute inset-x-0 bottom-0 h-2/5"
                  style={{
                    background: `linear-gradient(to bottom, transparent, ${cardColor})`,
                  }}
                />
              </motion.div>

              {/* Share Button */}
              <motion.div>
                <Button className="absolute right-4 top-4 flex size-10 min-w-10 items-center justify-center rounded-full bg-primary/90 p-2 text-gray-900 shadow-lg backdrop-blur-sm transition-all hover:scale-105 hover:bg-white">
                  <MdShare className="size-5" />
                </Button>
              </motion.div>

              {/* Profile Picture */}
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2">
                <motion.div className="relative size-28 overflow-hidden rounded-full border-2 border-linko-dark/30 bg-primary shadow-lg transition-transform">
                  <Image
                    defaultImage={fallbackAvatarImage}
                    src={card.avatar}
                    alt="Profile"
                    className="size-full object-cover"
                    refreshKey={card.updated_at}
                  />
                </motion.div>
              </div>
            </div>

            {/* Name and Job */}
            <motion.div className="px-4 pt-16 text-center">
              <Text className="text-lg font-semibold text-linko-dark">
                {card?.full_name}
              </Text>
              <Text className="mt-1 text-xs font-medium text-gray-dark">
                {card?.job}
              </Text>
              <Text className="mt-0.5 text-xs text-gray-dark">
                {card?.company}
              </Text>

              {/* Add Buttons - Place directly below profile info */}
              <div className="mt-4 flex justify-center gap-2">
                <Button className="w-[125px] rounded-full">
                  <FaUserPlus className="-ml-0.5 size-4" />
                  {t('updateCard:phone.addButton')}
                </Button>
                <Button className="w-[125px] rounded-full bg-linko-light">
                  <FaExchangeAlt className="-ml-0.5 size-4" />
                  {t('digitalCard:exchange.button')}
                </Button>
              </div>
            </motion.div>

            {/* Content Section */}
            <div className="px-4">
              <div className="space-y-6 py-6">
                {/* Biography */}
                {card?.biography && (
                  <motion.div
                    className="rounded-2xl bg-gray-light/50 p-5 shadow-sm"
                    style={{}}
                  >
                    <Text className="text-xs leading-relaxed text-gray-dark">
                      {card?.biography}
                    </Text>
                  </motion.div>
                )}

                {/* Social Links */}
                <motion.div className="grid grid-cols-3 gap-x-4 gap-y-6">
                  {card.links
                    ?.filter(link => link.is_active)
                    ?.map((link, index) => {
                      return (
                        <div
                          key={index}
                          className="flex cursor-pointer flex-col items-center gap-2 rounded-xl"
                        >
                          <LinkIcon
                            link={link}
                            iconStyle="size-16 rounded-[16px] aspect-square shadow-sm"
                            emojiStyle="text-4xl size-16 rounded-[16px] aspect-square"
                          />
                          <Text className="text-center text-xs font-medium text-gray-dark">
                            {link.title}
                          </Text>
                        </div>
                      );
                    })}
                </motion.div>
              </div>
            </div>
          </div>
        </div>

        {/* Lead Capture Modal - Visible uniquement dans l'onglet lead-capture */}
        {isLeadCaptureTab && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/50 p-4">
            <div className="w-full max-w-[250px] overflow-hidden rounded-lg bg-white p-4 shadow-2xl">
              <div className="mb-3">
                <Text className="mb-1 font-medium text-gray-900">
                  {leadCaptureTitle}
                </Text>
                <Text className="text-xs text-gray-600">
                  {leadCaptureDescription}
                </Text>
              </div>

              {/* Champs de formulaire simplifiés */}
              <div className="mb-3 space-y-1.5">
                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-600">
                    Nom complet
                    <span className="ml-1 text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Nom complet"
                    className="w-full rounded border border-gray-300 bg-white px-2 py-1 text-xs focus:outline-none"
                    readOnly
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-600">
                    Email
                    <span className="ml-1 text-danger">*</span>
                  </label>
                  <input
                    type="email"
                    placeholder="Email"
                    className="w-full rounded border border-gray-300 bg-white px-2 py-1 text-xs focus:outline-none"
                    readOnly
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-600">
                    Téléphone
                  </label>
                  <input
                    type="tel"
                    placeholder="Téléphone"
                    className="w-full rounded border border-gray-300 bg-white px-2 py-1 text-xs focus:outline-none"
                    readOnly
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-600">
                    Entreprise
                  </label>
                  <input
                    type="text"
                    placeholder="Entreprise"
                    className="w-full rounded border border-gray-300 bg-white px-2 py-1 text-xs focus:outline-none"
                    readOnly
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-600">
                    Message
                  </label>
                  <textarea
                    placeholder="Message"
                    className="h-14 w-full resize-none rounded border border-gray-300 bg-white px-2 py-1 text-xs focus:outline-none"
                    readOnly
                  ></textarea>
                </div>
              </div>

              {/* Bouton */}
              <Button className="w-full rounded-full bg-linko-light py-1.5 text-xs">
                {leadCaptureButtonText}
              </Button>

              {/* Mention légale */}
              {leadCaptureDisclaimer && (
                <Text className="mt-2 text-left text-[10px] text-gray-500">
                  {leadCaptureDisclaimer}
                </Text>
              )}
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );

  return isLimitExceeded ? (
    <PremiumFeatureOverlay
      title={t('digitalCard:visit-limit-exceeded')}
      message={t('digitalCard:upgrade-subscription')}
      showUpgradeButton={!!user}
      className="rounded-2xl"
    >
      {phoneContent}
    </PremiumFeatureOverlay>
  ) : (
    phoneContent
  );
};
